import { Section } from '@insify/components';
import { Button } from '@insify/design-system/components/Button';
import { ArrowLeft, ChevronLeft } from '@insify/design-system/icons';
import { useBreakpoint } from '@insify/react-hooks';
import { getPortalDOM } from '@insify/ui-utils';
import { TrackedDiv } from '@objectiv/tracker-react';
import clsx from 'clsx';
import ReactDOM from 'react-dom';

import * as styles from './Layout.module.css';
import { useFFMobileBottomBarWorkaround } from './useFFMobileBottomBarWorkaround';

import type { ButtonProps } from '@insify/design-system/components/Button';

export type LayoutBackProps = {
  backCopy: string;
  onBack: () => void;
};

type LayoutSubmitProps = {
  submitCopy: string;
  isLoading?: boolean;
  isDisabled?: boolean;
};

const WIZARD_FORM_ID = 'WIZARD_FORM_ID';
const BOTTOM_BAR_PORTAL_ID = 'funnel-navigation-footer';

export type LayoutWrapperProps = {
  className?: string;
  children: React.ReactNode;
};

type Wrapper = React.FC<LayoutWrapperProps>;

const Layout = () => {
  throw new Error('Do NOT use this as a component');
};

type LayoutComponentProps = { className?: string };

/** Will only provide a div if className is defined */
Layout.DesktopOnly = (props: { children: React.ReactNode; className?: string }) => {
  const isDesktop = useBreakpoint('(min-width: 960px)');
  const hasWrapper = props.className !== undefined;
  if (!isDesktop) {
    return null;
  }
  if (hasWrapper) {
    return <div {...props} />;
  }

  return <>{props.children}</>;
};

/** Will only provide a div if className is defined */
Layout.MobileOnly = (props: { children: React.ReactNode; className?: string }) => {
  const isMobile = !useBreakpoint('(min-width: 960px)');
  const hasWrapper = props.className !== undefined;
  if (!isMobile) {
    return null;
  }
  if (hasWrapper) {
    return <div {...props} />;
  }

  return <>{props.children}</>;
};

Layout.Form = ({
  Component,
  className,
  onSubmit,
  children,
}: {
  Component?: Wrapper;
  className?: string;
  children: React.ReactNode;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
}) => {
  const Form = Component ?? 'form';

  return (
    <Form className={className} onSubmit={onSubmit} id={WIZARD_FORM_ID} noValidate>
      {children}
    </Form>
  );
};

Layout.Submit = ({ submitCopy, isLoading, isDisabled, ...props }: LayoutSubmitProps & LayoutComponentProps) => {
  return (
    <Button
      {...props}
      trackingId='continue'
      type='submit'
      kind='primary'
      disabled={isDisabled}
      loading={isLoading}
      data-testid='submit-form-btn'
      // The submit button is rendered in portal outside the form.
      // This is done because the parent container of the form has transform property applied to it,
      // and it causes the "position: fixed" to not work as expected.
      // To link the button to the form, we need to pass the form id to the button.
      form={WIZARD_FORM_ID}
    >
      {submitCopy}
    </Button>
  );
};

Layout.SubmitFullWidth = ({ className, ...props }: LayoutSubmitProps & LayoutComponentProps) => (
  <Layout.Submit className={clsx(className, styles.submitFullWidth)} {...props} />
);

Layout.SubmitDesktop = ({ className, ...props }: LayoutSubmitProps & LayoutComponentProps) => (
  <Layout.DesktopOnly className={clsx(className, styles.submitDesktop)}>
    <Layout.SubmitFullWidth {...props} />
  </Layout.DesktopOnly>
);

Layout.SubmitDesktopSticky = ({ className, ...props }: LayoutSubmitProps & LayoutComponentProps) => (
  <Layout.DesktopOnly className={clsx(className, styles.submitDesktopSticky)}>
    <Layout.SubmitFullWidth {...props} />
  </Layout.DesktopOnly>
);

Layout.Back = ({ backCopy, onBack, ...props }: LayoutBackProps & Partial<Pick<ButtonProps, 'kind' | 'className'>>) => (
  <Button
    trackingId='back'
    type='button'
    kind='tertiary'
    data-testid='back-form-btn'
    leftIcon={<ChevronLeft />}
    onClick={onBack}
    {...props}
  >
    {backCopy}
  </Button>
);

Layout.BottomBar = ({
  children,
  className,
  extraContent,
}: LayoutComponentProps & { children: React.ReactNode; extraContent?: React.ReactNode }) => {
  useFFMobileBottomBarWorkaround(BOTTOM_BAR_PORTAL_ID);

  return ReactDOM.createPortal(
    <TrackedDiv objectiv={{ id: 'fixed-bar' }} className={clsx(styles.fixedBar, className)}>
      {extraContent && <Section>{extraContent}</Section>}
      <Section>
        <div className={styles.fixedBarContent}>{children}</div>
      </Section>
    </TrackedDiv>,
    getPortalDOM(BOTTOM_BAR_PORTAL_ID),
  );
};

Layout.BackSmall = ({ backCopy, onBack, ...props }: LayoutBackProps & LayoutComponentProps) => {
  return (
    <Button
      {...props}
      aria-label={backCopy}
      className={styles.buttonSmall}
      trackingId='back'
      type='button'
      kind='secondary'
      // Can have duplicate data-testid as only one Back button is present at the time
      data-testid='back-form-btn'
      leftIcon={<ArrowLeft />}
      onClick={onBack}
    />
  );
};

Layout.NavContainerDesktop = ({ className, children }: LayoutComponentProps & { children: React.ReactNode }) => (
  <Layout.DesktopOnly className={clsx(className, styles.desktopNav)}>{children}</Layout.DesktopOnly>
);

export { Layout };
